import {
  Root,
  Trigger,
  Portal,
  Overlay,
  Content,
  Title,
  Description,
  Close,
} from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "../../lib/cn";

// Its own function, not Root itself: Dialog shares the Radix Root, and the
// Object.assign below must not overwrite Dialog's parts (see Dialog.tsx).
function SheetRoot(props: ComponentProps<typeof Root>) {
  return <Root {...props} />;
}
const SheetTrigger = Trigger;
const SheetClose = Close;

function SheetContent({
  side = "right",
  className,
  children,
}: {
  side?: "right" | "left";
  className?: string;
  children: ReactNode;
}) {
  const sideClass =
    side === "right"
      ? "right-0 inset-y-0 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
      : "left-0 inset-y-0 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left";
  return (
    <Portal>
      <Overlay
        className={cn(
          "fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=open]:fade-in data-[state=closed]:fade-out",
        )}
      />
      <Content
        className={cn(
          "fixed z-50 flex flex-col border-neutral-800 bg-neutral-950 shadow-lg",
          "w-full max-w-md md:max-w-lg",
          "max-md:!max-w-none max-md:inset-0 max-md:border-0",
          sideClass,
          "data-[state=open]:animate-in data-[state=closed]:animate-out duration-200",
          className,
        )}
      >
        {children}
      </Content>
    </Portal>
  );
}

function SheetHeader({
  title,
  description,
  eyebrow,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  eyebrow?: ReactNode;
  className?: string;
}) {
  return (
    <header
      className={cn(
        "flex items-start justify-between gap-3 border-b border-neutral-800 px-5 py-4",
        className,
      )}
    >
      <div className="min-w-0">
        {eyebrow ? (
          <p className="text-[11px] uppercase tracking-wider text-neutral-500">
            {eyebrow}
          </p>
        ) : null}
        <Title className="mt-0.5 truncate text-base font-semibold text-neutral-100">
          {title}
        </Title>
        {description ? (
          <Description className="mt-1 text-xs text-neutral-400">
            {description}
          </Description>
        ) : null}
      </div>
      <Close
        aria-label="Close panel"
        className="rounded-md p-1 text-neutral-400 hover:bg-neutral-900 hover:text-neutral-100 focus-visible:outline-2 focus-visible:outline-accent-500"
      >
        <X size={16} />
      </Close>
    </header>
  );
}

function SheetBody({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("min-h-0 flex-1 overflow-y-auto px-5 py-4", className)}>
      {children}
    </div>
  );
}

function SheetFooter({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <footer
      className={cn(
        "flex items-center justify-end gap-2 border-t border-neutral-800 px-5 py-3",
        className,
      )}
    >
      {children}
    </footer>
  );
}

export const Sheet = Object.assign(SheetRoot, {
  Trigger: SheetTrigger,
  Content: SheetContent,
  Header: SheetHeader,
  Body: SheetBody,
  Footer: SheetFooter,
  Close: SheetClose,
});
